import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, LogIn, Loader2, AlertCircle } from "lucide-react";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------------------------------------------------------- login page */
export const AdminLogin = () => {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        if (r.data?.is_admin) navigate("/admin", { replace: true });
        else setChecking(false);
      })
      .catch(() => setChecking(false));
  }, [navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("denied")) {
      setError("That Google account does not have admin access.");
    }
  }, []);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
      redirectUrl
    )}`;
  };

  return (
    <>
      <Seo
        title="Admin sign in | InZoRAH"
        description="Private administration area."
        path="/admin/login"
        noindex
      />
      <div className="adm-login">
        <div className="adm-login-card">
          <div className="adm-lock">
            <ShieldCheck size={22} aria-hidden="true" />
          </div>
          <h1>InZoRAH admin</h1>
          <p>
            Private dashboard. Sign in with the Google account on the admin list —
            there is no public sign-up.
          </p>
          {error && (
            <p className="adm-error">
              <AlertCircle size={14} aria-hidden="true" /> {error}
            </p>
          )}
          <button type="button" className="adm-google" onClick={signIn} disabled={checking}>
            {checking ? (
              <>
                <Loader2 size={16} className="adm-spin" aria-hidden="true" /> Checking session…
              </>
            ) : (
              <>
                <LogIn size={16} aria-hidden="true" /> Continue with Google
              </>
            )}
          </button>
          <p className="adm-fine">
            Sessions last 7 days and are stored in a secure, http-only cookie.
          </p>
        </div>
      </div>
    </>
  );
};

/* ------------------------------------------------------- auth callback */
export const AuthCallback = () => {
  const hasProcessed = useRef(false);
  const [failed, setFailed] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const sessionId = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
    if (!sessionId) {
      window.location.replace("/admin/login");
      return;
    }

    axios
      .post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then(() => {
        window.history.replaceState({}, document.title, "/admin");
        window.location.replace("/admin");
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (status === 403) {
          window.location.replace("/admin/login?denied=1");
        } else {
          setFailed(
            err?.response?.data?.detail || "Sign-in failed. Please try again."
          );
        }
      });
  }, []);

  return (
    <div className="adm-login">
      <div className="adm-login-card">
        {failed ? (
          <>
            <h1>Sign-in failed</h1>
            <p>{failed}</p>
            <a className="adm-google" href="/admin/login">
              Try again
            </a>
          </>
        ) : (
          <>
            <Loader2 size={22} className="adm-spin" aria-hidden="true" />
            <h1>Signing you in…</h1>
            <p>One moment.</p>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------ protected route */
export const RequireAdmin = ({ children }) => {
  const [state, setState] = useState(null); // null = checking
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes("session_id=")) return;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        if (r.data?.is_admin) {
          setUser(r.data);
          setState(true);
        } else {
          setState(false);
        }
      })
      .catch(() => setState(false));
  }, []);

  if (state === null) {
    return (
      <div className="adm-login">
        <div className="adm-login-card">
          <Loader2 size={22} className="adm-spin" aria-hidden="true" />
          <h1>Checking access…</h1>
        </div>
      </div>
    );
  }
  if (state === false) {
    window.location.replace("/admin/login");
    return null;
  }
  return React.cloneElement(children, { user });
};
